import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Stethoscope, NotebookPen, BookOpen, LifeBuoy, ShieldCheck, ArrowRight,
  Sparkles, Activity, FileText, FlaskConical, Check, HeartHandshake,
} from "lucide-react";
import AsaLogo from "../components/AsaLogo";
import { IMAGES, TIERS, gbp, CONDITION_DIRECTORY_COUNT, CONTACT_EMAIL } from "../data";

function Feature({ icon: Icon, title, desc }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
      <div className="w-11 h-11 rounded-lg bg-[#e6f4f2] text-[#0d9488] flex items-center justify-center mb-3">
        <Icon size={22} />
      </div>
      <h3 className="font-head font-semibold text-slate-800">{title}</h3>
      <p className="text-[14px] text-slate-500 mt-1 leading-relaxed">{desc}</p>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#f4f8f7]">
      {/* Nav */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <AsaLogo size={32} />
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/asa/login")} className="h-10 px-4 rounded-lg text-slate-600 font-semibold text-sm hover:bg-slate-100 transition-colors">Log In</button>
            <button onClick={() => navigate("/asa/register")} className="h-10 px-4 rounded-lg bg-[#0d9488] hover:bg-[#0f766e] text-white font-semibold text-sm transition-colors">Join now</button>
          </div>
        </div>
      </header>

      {/* Welcome / Introduction */}
      <section className="max-w-4xl mx-auto px-4 pt-12" data-testid="welcome-intro">
        <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#0f766e] bg-[#e6f4f2] px-3 py-1 rounded-full">
          <Sparkles size={14} /> Welcome
        </span>
        <h2 className="font-head font-bold text-slate-800 text-3xl sm:text-4xl leading-tight mt-4">
          Welcome to ASAL
        </h2>
        <p className="font-head text-lg md:text-xl text-[#0f766e] font-semibold mt-2">
          Autoimmune Symptoms, Awareness &amp; Learning
        </p>
        <div className="space-y-4 mt-6 text-slate-600 leading-relaxed">
          <p>
            ASAL is your intelligent autoimmune health companion, designed to help you better understand your symptoms,
            family history, blood test results and lifestyle factors that may be associated with autoimmune disease.
            <strong className="text-slate-800"> ASAL is not a diagnostic tool and does not replace advice from a qualified healthcare professional.</strong>
            {" "}Instead, it analyses the information you provide to identify patterns and estimate whether your health profile
            may be consistent with an autoimmune condition, helping you decide whether further medical assessment may be appropriate.
          </p>
          <p>
            With ASAL, you can record and monitor your symptoms over time, track changes in your health, upload and store your
            blood test results, identify possible symptom triggers, and build a personalised health record. The app also provides
            evidence-informed information about inflammatory foods, food additives, E numbers and lifestyle factors that may
            influence inflammation, together with practical educational resources to support informed health decisions.
          </p>
          <p>
            As you continue to use ASAL, it builds a clearer picture of your health journey, helping you recognise trends,
            prepare for medical appointments and monitor your progress over time. Whether you are searching for answers or already
            living with an autoimmune condition, ASAL helps you better understand your health through ongoing symptom tracking,
            education and personalised insights.
          </p>
        </div>
        <div className="border-t border-slate-200 mt-10" />
      </section>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 pt-12 pb-16 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#0f766e] bg-[#e6f4f2] px-3 py-1 rounded-full">
            <Sparkles size={14} /> Support for {CONDITION_DIRECTORY_COUNT} autoimmune conditions
          </span>
          <h1 className="font-head font-bold text-slate-800 text-4xl sm:text-5xl leading-tight mt-4">
            Take control of your autoimmune health
          </h1>
          <p className="text-slate-500 text-lg mt-4 leading-relaxed">
            Track your symptoms, spot patterns in your blood results, and walk into your next GP appointment with a clear, clinician-ready summary — all in one private place, built by the ASA Foundation.
          </p>
          <div className="flex flex-wrap gap-3 mt-7">
            <button data-testid="hero-cta-start" onClick={() => navigate("/asa/register")} className="inline-flex items-center gap-2 h-12 px-6 rounded-lg bg-[#0d9488] hover:bg-[#0f766e] text-white font-semibold transition-colors">
              Start free today <ArrowRight size={18} />
            </button>
            <button onClick={() => navigate("/asa/login")} className="h-12 px-6 rounded-lg border border-slate-300 text-slate-700 font-semibold hover:bg-white transition-colors">
              I have an account
            </button>
          </div>
          <p className="text-[12px] text-slate-400 mt-4 flex items-center gap-1.5">
            <ShieldCheck size={14} /> Private & secure · Not a medical diagnosis · Cancel anytime
          </p>
        </div>
        <div className="relative">
          <img src={IMAGES.hero} alt="Person managing their wellbeing at home" className="rounded-2xl w-full h-[420px] object-cover shadow-lg" />
        </div>
      </section>

      {/* How it works — 3 steps */}
      <section className="max-w-6xl mx-auto px-4 pb-4" data-testid="how-it-works">
        <h2 className="font-head font-bold text-slate-800 text-2xl text-center">How ASAL works — in 3 simple steps</h2>
        <p className="text-center text-slate-500 mt-2 max-w-2xl mx-auto">From first symptom to a conversation with your doctor — ASAL guides you the whole way.</p>
        <div className="grid md:grid-cols-3 gap-5 mt-8">
          {[
            { n: 1, icon: Stethoscope, title: "Check", desc: `Run the Symptom Checker, which draws on our directory of ${CONDITION_DIRECTORY_COUNT} autoimmune conditions and your family history to see where your symptoms may point.` },
            { n: 2, icon: NotebookPen, title: "Track", desc: "Log symptoms daily and upload your blood test results — ASAL reveals patterns, trends and possible triggers over time." },
            { n: 3, icon: FileText, title: "Share", desc: "Generate a clear, clinician-ready summary to bring to your GP, so you get the right assessment sooner." },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.n} className="relative bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-md transition-shadow" data-testid={`step-${s.n}`}>
                <span className="absolute -top-3 -left-3 w-9 h-9 rounded-full bg-[#0d9488] text-white font-head font-bold flex items-center justify-center shadow-sm">{s.n}</span>
                <div className="w-12 h-12 rounded-xl bg-[#e6f4f2] text-[#0d9488] flex items-center justify-center"><Icon size={24} /></div>
                <h3 className="font-head font-semibold text-slate-800 text-lg mt-4">{s.title}</h3>
                <p className="text-[14px] text-slate-500 mt-1.5 leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
        </div>
        <div className="flex items-start justify-center gap-2 mt-6 max-w-2xl mx-auto text-center">
          <ShieldCheck size={16} className="text-[#0d9488] shrink-0 mt-0.5" />
          <p className="text-[13px] text-slate-500">Your data is private and encrypted, visible only to you. It's only ever shared <strong className="text-slate-700">anonymously and with your consent</strong> to help fund autoimmune research. ASAL is an awareness tool — not a medical diagnosis.</p>
        </div>
      </section>

      {/* Essential (free) features */}
      <section className="max-w-6xl mx-auto px-4 pb-14">
        <h2 className="font-head font-bold text-slate-800 text-2xl text-center">Everything you need to get started</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          <Feature icon={Stethoscope} title="Symptom Checker" desc="Answer A–Z symptom questions plus family history for a clearer indication of possible conditions." />
          <Feature icon={NotebookPen} title="Symptom Diary" desc="Log daily symptoms, severity and episodes to spot patterns over time." />
          <Feature icon={BookOpen} title="Education Library" desc="Trusted, plain-English guides on living well with autoimmune disease." />
          <Feature icon={LifeBuoy} title="ASAL Support" desc="Reach the ASA Foundation team and community whenever you need help." />
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-5xl mx-auto px-4 pb-16">
        <div className="bg-gradient-to-r from-[#0d9488] to-[#0f766e] rounded-2xl p-6 sm:p-7 text-white flex items-start gap-4 mb-10" data-testid="charity-mission-banner">
          <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center shrink-0"><HeartHandshake size={26} /></div>
          <div>
            <h3 className="font-head font-bold text-lg">100% of subscriptions fund the ASA Foundation</h3>
            <p className="text-white/90 text-sm mt-1 leading-relaxed">Every membership goes directly to the ASA Foundation, a charity working to bring <strong>early autoimmune diagnostics to vulnerable people — including children</strong>. By subscribing, you're not just taking control of your own health; you're helping fund earlier diagnosis for those who need it most.</p>
          </div>
        </div>
        <h2 className="font-head font-bold text-slate-800 text-2xl text-center">Membership tiers</h2>
        <p className="text-center text-slate-500 mt-2">All funds from subscriptions go directly to the ASA Foundation to support early diagnostics for vulnerable people, such as children. Advanced tools are included with ASAL Pro.</p>
        <div className="grid sm:grid-cols-2 gap-5 mt-8 max-w-3xl mx-auto">
          {TIERS.map((t) => (
            <div key={t.id} className={`relative bg-white rounded-2xl border p-6 ${t.id === "pro" ? "border-[#0d9488] shadow-md" : "border-slate-200"}`}>
              {t.badge && <span className="absolute -top-3 left-6 text-[12px] font-semibold text-white bg-[#0d9488] px-3 py-1 rounded-full">{t.badge}</span>}
              <h3 className="font-head font-semibold text-slate-700">{t.name}</h3>
              <p className="text-[12.5px] text-slate-400">{t.tagline}</p>
              <div className="flex items-end gap-1 mt-2">
                {t.monthly === 0 ? <span className="text-3xl font-head font-bold text-[#0d9488]">Free</span> : <><span className="text-3xl font-head font-bold text-slate-800">{gbp(t.monthly)}</span><span className="text-slate-400 mb-1">/month</span></>}
              </div>
              {t.monthly !== 0 && <p className="text-[13px] text-[#0f766e] font-medium mt-1 flex items-center gap-2 flex-wrap">or {gbp(t.yearly)}/year <span className="text-[10.5px] font-bold text-[#22a35a] bg-[#eafaf1] px-1.5 py-0.5 rounded-full">2 MONTHS FREE</span></p>}
              <ul className="mt-4 space-y-2 text-[13.5px] text-slate-600">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2"><Check size={16} className="text-[#0d9488] mt-0.5 shrink-0" /> {f}</li>
                ))}
              </ul>
              <button onClick={() => navigate(`/asa/register?tier=${t.id}`)} className={`w-full mt-5 h-11 rounded-lg font-semibold transition-colors ${t.id === "pro" ? "bg-[#0d9488] hover:bg-[#0f766e] text-white" : "border-2 border-[#0d9488] text-[#0f766e] hover:bg-[#e6f4f2]"}`}>{t.id === "essential" ? "Start free" : "Get ASAL Pro"}</button>
            </div>
          ))}
        </div>
        <p className="text-center text-[13px] text-slate-500 mt-4"><button onClick={() => navigate("/asa/pricing")} className="text-[#0d9488] font-semibold hover:underline">Compare plans in detail →</button></p>
        <p className="text-center text-[12.5px] text-slate-400 mt-2">ASAL is a health education & awareness tool. It does not diagnose or calculate an individual's risk of disease. Sign-up includes consent to anonymous data sharing.</p>
      </section>

      {/* Included with ASAL Pro */}
      <section className="max-w-6xl mx-auto px-4 pb-16">
        <h2 className="font-head font-bold text-slate-800 text-2xl text-center">Powerful tools — available today</h2>
        <p className="text-center text-slate-500 mt-2 max-w-2xl mx-auto">All included with ASAL Pro. Upgrade any time to unlock everything.</p>
        <div className="grid md:grid-cols-3 gap-4 mt-8">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#22a35a] bg-[#eafaf1] px-2 py-0.5 rounded-full"><Check size={12} /> Included with ASAL Pro</span>
            <div className="flex gap-3 mt-3 text-[#0d9488]"><Sparkles size={20}/><Activity size={20}/><FileText size={20}/></div>
            <h3 className="font-head font-semibold text-slate-800 mt-3">AI insights & GP reports</h3>
            <p className="text-[14px] text-slate-600 mt-1">AI insights, symptom forecasting and clinician-ready GP / Doctor reports.</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#22a35a] bg-[#eafaf1] px-2 py-0.5 rounded-full"><Check size={12} /> Included with ASAL Pro</span>
            <div className="flex gap-3 mt-3 text-[#0d9488]"><FlaskConical size={20}/></div>
            <h3 className="font-head font-semibold text-slate-800 mt-3">Clinical-trial matching</h3>
            <p className="text-[14px] text-slate-600 mt-1">Live clinical-trial matching from ClinicalTrials.gov — helping people who are already diagnosed find and join relevant studies near them.</p>
          </div>
          <div className="bg-[#e6f4f2] rounded-xl border border-[#c9e9e4] p-5 flex flex-col">
            <span className="text-[12px] font-bold text-[#0f766e]">UNLOCK EVERYTHING</span>
            <p className="text-[14px] text-slate-700 mt-3 font-medium">All ASAL Pro tools are available today. Choose a plan to unlock everything now.</p>
            <button onClick={() => navigate("/asa/register")} className="mt-auto pt-4 self-start"><span className="inline-flex h-10 px-4 items-center rounded-lg bg-[#0d9488] hover:bg-[#0f766e] text-white text-sm font-semibold transition-colors">Choose a plan</span></button>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-8 text-center">
          <AsaLogo size={30} />
          <p className="text-[13px] text-slate-500 mt-4">This app provides general information and is not a medical diagnosis. Always consult a qualified healthcare professional.</p>
          <p className="text-[13px] text-slate-500 mt-2">Contact: <a href={`mailto:${CONTACT_EMAIL}`} className="font-semibold text-[#0f766e] hover:underline">{CONTACT_EMAIL}</a></p>
          <p className="text-[12px] text-slate-400 mt-2">© {new Date().getFullYear()} ASA Foundation. All rights reserved. Copyright &amp; IP: Asal Shirazi.</p>
        </div>
      </footer>
    </div>
  );
}
